// Generat automat din README.md - Advanced Search & Filtering System
import React, { useState, useMemo } from 'react';
import { Search, Filter, X, ChevronDown, Calendar, DollarSign } from 'lucide-react';

export const useAdvancedSearch = (data, searchableFields = []) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({ key: '', direction: 'asc' });
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const filteredData = useMemo(() => {
    let result = [...data];

    // Text search
    if (searchTerm && searchableFields.length > 0) {
      result = result.filter(item =>
        searchableFields.some(field =>
          item[field] && 
          item[field].toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply filters
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key] !== '' && filters[key] !== 'all') {
        result = result.filter(item => {
          const itemValue = item[key];
          const filterValue = filters[key];
          
          if (Array.isArray(filterValue)) {
            return filterValue.includes(itemValue);
          }
          
          return itemValue === filterValue;
        });
      }
    });

    // Date range filtering
    if (dateRange.start || dateRange.end) {
      result = result.filter(item => {
        const itemDate = new Date(item.createdAt || item.date || item.lastContact);
        const startDate = dateRange.start ? new Date(dateRange.start) : null;
        const endDate = dateRange.end ? new Date(dateRange.end) : null;
        
        if (startDate && itemDate < startDate) return false;
        if (endDate && itemDate > endDate) return false;
        
        return true;
      });
    }

    // Apply sorting
    if (sorting.key) {
      result.sort((a, b) => {
        const aVal = a[sorting.key] || '';
        const bVal = b[sorting.key] || '';
        
        // Handle different data types
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sorting.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        // Handle dates
        if (Date.parse(aVal) && Date.parse(bVal)) {
          const dateA = new Date(aVal);
          const dateB = new Date(bVal);
          return sorting.direction === 'asc' ? dateA - dateB : dateB - dateA;
        }
        
        // Handle strings
        const comparison = aVal.toString().localeCompare(bVal.toString());
        return sorting.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchTerm, filters, sorting, dateRange, searchableFields]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({});
    setSorting({ key: '', direction: 'asc' });
    setDateRange({ start: '', end: '' });
  };

  const hasActiveFilters = searchTerm || Object.keys(filters).length > 0 || dateRange.start || dateRange.end;

  return {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    sorting,
    setSorting,
    dateRange,
    setDateRange,
    filteredData,
    clearFilters,
    hasActiveFilters
  };
};

// Advanced Search Component
const AdvancedSearchBar = ({
  searchTerm,
  onSearchChange,
  filters,
  onFiltersChange,
  availableFilters = [],
  onClearFilters,
  hasActiveFilters,
  placeholder = "Caută..."
}) => {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
        />
        
        {/* Filter Toggle */}
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Șterge toate filtrele"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-1 rounded ${showFilters ? 'text-primary-600' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
            title="Filtre avansate"
          >
            <Filter className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableFilters.map(filter => (
              <div key={filter.key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {filter.label}
                </label>
                
                {filter.type === 'select' && (
                  <select
                    value={filters[filter.key] || ''}
                    onChange={(e) => onFiltersChange({
                      ...filters,
                      [filter.key]: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="">Toate</option>
                    {filter.options?.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
                
                {filter.type === 'date' && (
                  <input
                    type="date"
                    value={filters[filter.key] || ''}
                    onChange={(e) => onFiltersChange({
                      ...filters,
                      [filter.key]: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
                  />
                )}
                
                {filter.type === 'number' && (
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters[`${filter.key}_min`] || ''}
                      onChange={(e) => onFiltersChange({
                        ...filters,
                        [`${filter.key}_min`]: e.target.value
                      })}
                      className="w-1/2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters[`${filter.key}_max`] || ''}
                      onChange={(e) => onFiltersChange({
                        ...filters,
                        [`${filter.key}_max`]: e.target.value
                      })}
                      className="w-1/2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-end mt-4">
            <button
              onClick={onClearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Resetează Filtrele
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchBar;